import dignity from 'dignity.js';
import { canonicalize, sha512 } from './certificates.js';

const { VDF } = dignity;

/** SPECS ladder — keep Sloth cheap in the browser so chat stays interactive. */
export const ANTI_SPAM_STEPS = Object.freeze({
  presence: 2,
  chat: 2,
  room: 2,
  challenge: 3,
  ops: 3
});

/** Tiny steps for Vitest / Playwright so suites stay fast. */
export const ANTI_SPAM_TEST_STEPS = Object.freeze({
  presence: 1,
  chat: 1,
  room: 1,
  challenge: 1,
  ops: 1
});

const PROOF_MAX_AGE_MS = 15 * 60 * 1000;

export function isAntiSpamTestMode() {
  if (import.meta.env?.MODE === 'test' || import.meta.env?.VITEST) {
    return true;
  }
  if ((import.meta.env?.VITE_E2E_RELAY_URL || '').trim()) {
    return true;
  }
  if ((import.meta.env?.VITE_ANTI_SPAM_TEST_STEPS || '').trim() === '1') {
    return true;
  }
  return false;
}

export function stepsForAction(action) {
  const key = String(action || 'ops');
  const table = isAntiSpamTestMode() ? ANTI_SPAM_TEST_STEPS : ANTI_SPAM_STEPS;
  return table[key] || table.ops;
}

/** Stable hex digest of a payload (excludes antiSpam field). */
export async function payloadHash(value) {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    const { antiSpam: _ignored, ...rest } = value;
    return sha512(canonicalize(rest));
  }
  return sha512(canonicalize(value));
}

function challengeFromHash(payloadDigest, action, steps) {
  return sha512(`dechess-antispam:v1:${action}:${steps}:${payloadDigest}`);
}

/**
 * Prove an action with a linear Sloth VDF mini-proof.
 * @returns {{ action, steps, challenge, proof, provedAt }}
 */
export async function proveAction({
  action = 'ops',
  payload,
  payloadDigest = null,
  steps = null,
  compute = (challenge, stepCount) => VDF.compute(challenge, BigInt(stepCount))
} = {}) {
  const resolvedAction = String(action || 'ops');
  const resolvedSteps = steps ?? stepsForAction(resolvedAction);
  const digest = payloadDigest || await payloadHash(payload ?? {});
  const challenge = await challengeFromHash(digest, resolvedAction, resolvedSteps);
  const proof = await compute(challenge, resolvedSteps);
  return {
    action: resolvedAction,
    steps: resolvedSteps,
    challenge,
    proof: String(proof),
    provedAt: Date.now()
  };
}

/**
 * Verify an action proof against the payload hash.
 */
export async function verifyAction(
  antiSpam,
  payloadOrDigest,
  {
    now = Date.now(),
    verify = (challenge, steps, proof) => VDF.verify(challenge, BigInt(steps), proof)
  } = {}
) {
  if (!antiSpam || typeof antiSpam !== 'object') {
    return false;
  }
  const action = String(antiSpam.action || '');
  const steps = Number(antiSpam.steps);
  const challenge = String(antiSpam.challenge || '');
  const proof = String(antiSpam.proof || '');
  const provedAt = Number(antiSpam.provedAt) || 0;
  if (!action || !Number.isFinite(steps) || steps < 1 || !challenge || !proof) {
    return false;
  }
  if (!provedAt || now - provedAt > PROOF_MAX_AGE_MS || provedAt > now + 60_000) {
    return false;
  }
  const minSteps = stepsForAction(action);
  if (steps < minSteps) {
    return false;
  }
  const digest = typeof payloadOrDigest === 'string'
    ? payloadOrDigest
    : await payloadHash(payloadOrDigest ?? {});
  const expectedChallenge = await challengeFromHash(digest, action, steps);
  if (challenge !== expectedChallenge) {
    return false;
  }
  try {
    return Boolean(await verify(challenge, steps, proof));
  } catch {
    return false;
  }
}

/** Attach a fresh antiSpam proof to a payload object (non-mutating copy). */
export async function withAntiSpam(payload, action) {
  const base = { ...(payload || {}) };
  delete base.antiSpam;
  const antiSpam = await proveAction({ action, payload: base });
  return { ...base, antiSpam };
}

/** True when payload carries a valid proof for the given action class. */
export async function hasValidAntiSpam(payload, expectedAction = null) {
  if (!payload?.antiSpam) {
    return false;
  }
  if (expectedAction && payload.antiSpam.action !== expectedAction) {
    return false;
  }
  return verifyAction(payload.antiSpam, payload);
}
